import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Button,
  Typography,
  Box,
  Chip,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Star,
  ShoppingCart,
  LocalDrink,
  ScreenShare,
  Redeem,
  EmojiEvents,
  SportsEsports,
} from '@mui/icons-material';
import CakeIcon from '@mui/icons-material/Cake';
import { motion } from 'framer-motion';

// Map string icon names to components
const iconMap = {
  LocalDrink,
  ScreenShare,
  Redeem,
  EmojiEvents,
  SportsEsports,
  CakeIcon,
  Star, // fallback
};

const StoreItemCard = ({ item, onPurchase, userPoints, getRarityColor, isPurchasing = false, disabled = false }) => {
  const canAfford = userPoints >= item.price;
  // Handle both component and string icons
  const IconComponent = typeof item.icon === 'string' ? iconMap[item.icon] || Star : item.icon;

  return (
    <Card
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        border: `3px solid ${getRarityColor(item.rarity)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 20px 40px rgba(0,0,0,0.1), 0 0 0 4px ${getRarityColor(item.rarity)}40`,
        },
      }}
    >
      {/* Featured Badge */}
      {item.featured && (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            zIndex: 2,
          }}
        >
          <Badge
            badgeContent="⭐"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: '#ffd700',
                color: '#000',
                fontSize: '16px',
                width: 30,
                height: 30,
                borderRadius: '50%',
              },
            }}
          />
        </Box>
      )}

      {/* Rarity Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 15,
          left: 15,
          zIndex: 2,
        }}
      >
        <Chip
          label={item.rarity.toUpperCase()}
          size="small"
          sx={{
            bgcolor: getRarityColor(item.rarity),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '10px',
            height: '20px',
          }}
        />
      </Box>

      {/* Item Image/Icon */}
      <Box
        sx={{
          position: 'relative',
          height: 140,
          background: `linear-gradient(135deg, ${getRarityColor(item.rarity)}20 0%, ${getRarityColor(item.rarity)}40 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {item.image ? (
          <CardMedia
            component="img"
            image={item.image}
            alt={item.name}
            sx={{
              width: '80%',
              height: '80%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))',
            }}
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = 'none';
            }}
          />
        ) : (
          /* Show Icon only when no image */
          <IconComponent
            sx={{
              fontSize: 60,
              color: getRarityColor(item.rarity),
            }}
          />
        )}

        {/* Shine Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            animation: 'shine 2s infinite',
            '@keyframes shine': {
              '0%': { left: '-100%' },
              '100%': { left: '100%' },
            },
          }}
        />
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Item Name */}
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{
            color: '#2c3e50',
            textAlign: 'center',
            mb: 0.5,
            fontSize: '1rem',
          }}
        >
          {item.name}
        </Typography>

        {/* Item Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            mb: 1.5,
            minHeight: '32px',
            fontSize: '0.875rem',
          }}
        >
          {item.description}
        </Typography>

        {/* Price Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            p: 0.8,
            bgcolor: '#f8f9fa',
            borderRadius: 2,
          }}
        >
          <Star sx={{ color: '#ffd700', mr: 1 }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              color: canAfford ? '#28a745' : '#dc3545',
            }}
          >
            {item.price} Points
          </Typography>
        </Box>

        {/* Purchase Button */}
        <motion.div whileTap={{ scale: 0.95 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={!canAfford || !item.inStock || isPurchasing || disabled}
            onClick={() => onPurchase(item)}
            startIcon={isPurchasing ? <CircularProgress size={20} color="inherit" /> : <ShoppingCart />}
            sx={{
              bgcolor: canAfford ? getRarityColor(item.rarity) : '#6c757d',
              '&:hover': {
                bgcolor: canAfford ? `${getRarityColor(item.rarity)}dd` : '#6c757d',
              },
              '&:disabled': {
                bgcolor: '#e9ecef',
                color: '#6c757d',
              },
              borderRadius: 2,
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '16px',
            }}
          >
            {isPurchasing
              ? 'Processing...'
              : !item.inStock
              ? 'Out of Stock'
              : !canAfford
              ? `Need ${item.price - userPoints} More`
              : 'Purchase'
            }
          </Button>
        </motion.div>

        {/* Stock Status */}
        {!item.inStock && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1,
              fontWeight: 'bold',
            }}
          >
            Currently Unavailable
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StoreItemCard;